<script setup lang="ts">
import type { HTMLAttributes } from 'vue';
import { watch } from 'vue';
import { useCurrencyInput, type CurrencyInputOptions } from 'vue-currency-input';
import { useRouter } from 'vue-router';
import { cn } from '@/lib/utils';

const props = defineProps<{
  defaultValue?: string | number | null;
  modelValue?: string | number | null;
  class?: HTMLAttributes['class'];
  options?: Partial<CurrencyInputOptions>;
  placeholder?: string;
}>();

const emits = defineEmits<{
  (e: 'update:modelValue', payload: string | number | null): void;
  (e: 'change', payload: string | number | null): void;
}>();

const router = useRouter();

const defaultOptions = {
  locale: router.currentRoute.value.params.locale === 'en' ? navigator.language : 'de-DE',
  currency: 'EUR',
  currencyDisplay:
    router.currentRoute.value.params.locale === 'en' ? ('code' as const) : ('symbol' as const),
  precision: {
    min: 0,
    max: 2,
  },
  hideCurrencySymbolOnFocus: true,
  hideGroupingSeparatorOnFocus: true,
  hideNegligibleDecimalDigitsOnFocus: true,
  autoDecimalDigits: false,
  valueAsInteger: false,
  autoSign: true,
  useGrouping: true,
  accountingSign: false,
} as CurrencyInputOptions;

// Handle precision override properly
const optionsWithPrecision = { ...props.options };
if (typeof props.options?.precision === 'number') {
  optionsWithPrecision.precision = {
    min: 0,
    max: props.options.precision,
  };
}

const currencyOptions = { ...defaultOptions, ...optionsWithPrecision };

const { inputRef, numberValue, setValue, setOptions } = useCurrencyInput(currencyOptions);

watch(
  () => props.modelValue,
  value => {
    if (value === undefined) return;
    const normalized = typeof value === 'string' ? parseFloat(value) || null : value;
    // Avoid resetting value (and caret) if nothing changed
    if (normalized === numberValue.value) return;
    setValue(normalized);
  },
  { immediate: true }
);

watch(
  () => props.options,
  options => {
    if (options) {
      setOptions({ ...defaultOptions, ...options });
    }
  }
);

watch(numberValue, value => {
  // Always emit the value, including null/undefined for proper validation
  emits('update:modelValue', value);
  emits('change', value);
});
</script>

<template>
  <input
    ref="inputRef"
    :class="
      cn(
        'tw-flex tw-h-9 tw-w-full tw-rounded-md tw-border tw-border-neutral-200 tw-bg-transparent tw-px-3 tw-py-1 tw-text-sm tw-shadow-sm tw-transition-colors file:tw-border-0 file:tw-bg-transparent file:tw-text-sm file:tw-font-medium placeholder:tw-text-neutral-500 focus-visible:tw-outline-none focus-visible:tw-ring-1 focus-visible:tw-ring-neutral-950 disabled:tw-cursor-not-allowed disabled:tw-opacity-50 dark:tw-border-neutral-800 dark:placeholder:tw-text-neutral-400 dark:focus-visible:tw-ring-neutral-300',
        props.class
      )
    "
    :placeholder="placeholder"
    type="text"
  />
</template>
